import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  LinearProgress,
  Chip,
  Checkbox,
  Stack,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckIcon,
  ErrorOutline as ErrorIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import {
  articlesAPI,
  categoriesAPI,
  unitsAPI,
} from '../services/api';
import { Article, ArticleCreate, Category, Unit } from '../types';
import { extractErrorMessage } from '../utils/errorHandler';
import { getModuleTableTheme, modernCellStyles } from '../theme/tableTheme';

type ArticleField = 'reference' | 'designation' | 'category' | 'unit' | 'seuil_alerte' | 'date_creation' | '__ignore__';

const FIELD_LABELS: Record<ArticleField, string> = {
  reference: 'Référence',
  designation: 'Désignation',
  category: 'Catégorie',
  unit: 'Unité',
  seuil_alerte: "Seuil d'alerte",
  date_creation: 'Date de création',
  __ignore__: '— Ignorer —',
};

const REQUIRED_FIELDS: ArticleField[] = ['reference', 'designation', 'category', 'unit'];

const normalize = (s: string): string =>
  s
    .toString()
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

const autoDetectField = (header: string): ArticleField => {
  const h = normalize(header);
  if (!h) return '__ignore__';
  if (h.includes('ref')) return 'reference';
  if (h.includes('design') || h.includes('libel') || h.includes('produit')) return 'designation';
  if (h.includes('categ')) return 'category';
  if (h.includes('unit')) return 'unit';
  if (h.includes('seuil') || h.includes('alert')) return 'seuil_alerte';
  if (h.includes('date')) return 'date_creation';
  return '__ignore__';
};

interface ParsedRow {
  rowIndex: number;
  cells: (string | number | null)[];
}

interface ImportRow {
  rowIndex: number;
  reference: string;
  designation: string;
  rawCategory: string;
  rawUnit: string;
  category_id: number | '';
  unit_id: number | '';
  seuil_alerte: string;
  date_creation: string;
  isDuplicate: boolean;
  skip: boolean;
  status: 'pending' | 'success' | 'error' | 'skipped';
  errorMessage?: string;
}

interface ArticleBulkImportDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onShowMessage?: (type: 'success' | 'error', message: string) => void;
}

const STEPS = ['Téléverser', 'Mapper les colonnes', 'Vérifier & corriger', 'Importer'];

const ArticleBulkImportDialog: React.FC<ArticleBulkImportDialogProps> = ({
  open,
  onClose,
  onSuccess,
  onShowMessage,
}) => {
  const [step, setStep] = useState(0);
  const [parsing, setParsing] = useState(false);
  const [fileName, setFileName] = useState<string>('');
  const [headers, setHeaders] = useState<string[]>([]);
  const [rawRows, setRawRows] = useState<ParsedRow[]>([]);
  const [columnMapping, setColumnMapping] = useState<ArticleField[]>([]);
  const [rows, setRows] = useState<ImportRow[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [existingReferences, setExistingReferences] = useState<Set<string>>(new Set());
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importDone, setImportDone] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const onShowMessageRef = useRef(onShowMessage);
  useEffect(() => {
    onShowMessageRef.current = onShowMessage;
  }, [onShowMessage]);

  const resetState = useCallback(() => {
    setStep(0);
    setParsing(false);
    setFileName('');
    setHeaders([]);
    setRawRows([]);
    setColumnMapping([]);
    setRows([]);
    setImporting(false);
    setImportDone(false);
    setProgress(0);
  }, []);

  useEffect(() => {
    if (!open) return;
    resetState();
    let cancelled = false;
    (async () => {
      setLoadingMeta(true);
      try {
        const [cats, uns, allArticles] = await Promise.all([
          categoriesAPI.getAll(),
          unitsAPI.getAll(),
          articlesAPI.getAllForDropdown(),
        ]);
        if (cancelled) return;
        setCategories(cats);
        setUnits(uns);
        setExistingReferences(
          new Set(allArticles.map((a: Article) => a.reference.trim().toLowerCase())),
        );
      } catch (err) {
        if (!cancelled) onShowMessageRef.current?.('error', extractErrorMessage(err));
      } finally {
        if (!cancelled) setLoadingMeta(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, resetState]);

  const categoryByName = useMemo(() => {
    const m = new Map<string, Category>();
    categories.forEach(c => m.set(normalize(c.name), c));
    return m;
  }, [categories]);

  const unitByName = useMemo(() => {
    const m = new Map<string, Unit>();
    units.forEach(u => m.set(normalize(u.name), u));
    return m;
  }, [units]);

  const handleFile = async (file: File) => {
    setParsing(true);
    try {
      const ExcelJS = (await import('exceljs')).default;
      const buf = await file.arrayBuffer();
      const wb = new ExcelJS.Workbook();
      await wb.xlsx.load(buf);
      const ws = wb.worksheets[0];
      if (!ws) throw new Error('Aucune feuille trouvée dans le fichier');

      const hdrRow = ws.getRow(1);
      const detectedHeaders: string[] = [];
      hdrRow.eachCell({ includeEmpty: false }, cell => {
        detectedHeaders.push(cell.text || '');
      });

      const parsedRows: ParsedRow[] = [];
      ws.eachRow({ includeEmpty: false }, (row, idx) => {
        if (idx === 1) return;
        const cells: (string | number | null)[] = [];
        for (let c = 1; c <= detectedHeaders.length; c++) {
          const cell = row.getCell(c);
          const v = cell.value;
          if (v == null) {
            cells.push(null);
          } else if (typeof v === 'object' && 'text' in (v as any)) {
            cells.push((v as any).text ?? '');
          } else if (typeof v === 'object' && 'result' in (v as any)) {
            cells.push(((v as any).result ?? '').toString());
          } else {
            cells.push(v as any);
          }
        }
        if (cells.some(c => c !== null && c !== '')) {
          parsedRows.push({ rowIndex: idx, cells });
        }
      });

      if (parsedRows.length === 0) {
        onShowMessage?.('error', 'Le fichier ne contient aucune ligne de données');
        return;
      }

      setFileName(file.name);
      setHeaders(detectedHeaders);
      setRawRows(parsedRows);
      setColumnMapping(detectedHeaders.map(autoDetectField));
      setStep(1);
    } catch (err) {
      onShowMessage?.('error', extractErrorMessage(err));
    } finally {
      setParsing(false);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) handleFile(f);
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  };

  const buildPreviewRows = () => {
    const idx = (field: ArticleField) => columnMapping.indexOf(field);
    const idxRef = idx('reference');
    const idxDes = idx('designation');
    const idxCat = idx('category');
    const idxUni = idx('unit');
    const idxSeuil = idx('seuil_alerte');
    const idxDate = idx('date_creation');
    const today = new Date().toISOString().split('T')[0];

    const built: ImportRow[] = rawRows.map(r => {
      const cellStr = (i: number): string =>
        i >= 0 && r.cells[i] != null ? String(r.cells[i]).trim() : '';
      const reference = cellStr(idxRef);
      const designation = cellStr(idxDes);
      const rawCategory = cellStr(idxCat);
      const rawUnit = cellStr(idxUni);
      const rawSeuil = cellStr(idxSeuil);
      const rawDate = cellStr(idxDate);

      const catMatch = rawCategory ? categoryByName.get(normalize(rawCategory)) : undefined;
      const unitMatch = rawUnit ? unitByName.get(normalize(rawUnit)) : undefined;

      const isDuplicate =
        !!reference && existingReferences.has(reference.toLowerCase());

      let date = today;
      if (rawDate) {
        const d = new Date(rawDate);
        if (!isNaN(d.getTime())) date = d.toISOString().split('T')[0];
      }

      const seuil =
        rawSeuil && !isNaN(parseFloat(rawSeuil.replace(',', '.')))
          ? rawSeuil.replace(',', '.')
          : '1';

      return {
        rowIndex: r.rowIndex,
        reference,
        designation,
        rawCategory,
        rawUnit,
        category_id: catMatch ? catMatch.id : '',
        unit_id: unitMatch ? unitMatch.id : '',
        seuil_alerte: seuil,
        date_creation: date,
        isDuplicate,
        skip: isDuplicate,
        status: 'pending',
      };
    });
    setRows(built);
    setStep(2);
  };

  const updateRow = (rowIdx: number, patch: Partial<ImportRow>) => {
    setRows(prev => prev.map((r, i) => (i === rowIdx ? { ...r, ...patch } : r)));
  };

  const validateRow = (r: ImportRow): string | null => {
    if (!r.reference.trim()) return 'Référence manquante';
    if (!r.designation.trim()) return 'Désignation manquante';
    if (r.category_id === '') return 'Catégorie non assignée';
    if (r.unit_id === '') return 'Unité non assignée';
    return null;
  };

  const importableRows = useMemo(
    () => rows.filter(r => !r.skip && validateRow(r) === null),
    [rows],
  );

  const blockingRows = useMemo(
    () => rows.filter(r => !r.skip && validateRow(r) !== null),
    [rows],
  );

  const handleImport = async () => {
    setStep(3);
    setImporting(true);
    setProgress(0);

    const target = rows
      .map((r, idx) => ({ r, idx }))
      .filter(({ r }) => !r.skip && validateRow(r) === null);

    let done = 0;
    let successCount = 0;
    let errorCount = 0;

    for (const { r, idx } of target) {
      try {
        const payload: ArticleCreate = {
          reference: r.reference.trim(),
          designation: r.designation.trim(),
          category_id: Number(r.category_id),
          unit_id: Number(r.unit_id),
          seuil_alerte: parseFloat(r.seuil_alerte) || 1,
          date_creation: r.date_creation,
        };
        await articlesAPI.create(payload);
        successCount += 1;
        updateRow(idx, { status: 'success' });
      } catch (err) {
        errorCount += 1;
        updateRow(idx, { status: 'error', errorMessage: extractErrorMessage(err) });
      }
      done += 1;
      setProgress((done / target.length) * 100);
    }

    setRows(prev =>
      prev.map(r => (r.skip && r.status === 'pending' ? { ...r, status: 'skipped' } : r)),
    );

    setImporting(false);
    setImportDone(true);
    if (successCount > 0) {
      onShowMessage?.(
        'success',
        `${successCount} article${successCount > 1 ? 's' : ''} importé${successCount > 1 ? 's' : ''} avec succès` +
          (errorCount > 0 ? ` — ${errorCount} erreur${errorCount > 1 ? 's' : ''}` : ''),
      );
      onSuccess();
    } else if (errorCount > 0) {
      onShowMessage?.('error', `Aucun article importé — ${errorCount} erreur${errorCount > 1 ? 's' : ''}`);
    }
  };

  const closeAfterImport = () => {
    onClose();
  };

  // ---------- Renderers ----------

  const renderUpload = () => (
    <Box
      onDragOver={e => e.preventDefault()}
      onDrop={handleDrop}
      sx={{
        border: '2px dashed',
        borderColor: 'primary.main',
        borderRadius: 2,
        p: 6,
        textAlign: 'center',
        bgcolor: 'action.hover',
        cursor: 'pointer',
      }}
      onClick={() => fileInputRef.current?.click()}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls"
        style={{ display: 'none' }}
        onChange={handleFileInput}
      />
      <CloudUploadIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
      <Typography variant="h6" gutterBottom>
        {parsing ? 'Lecture du fichier…' : 'Glisser-déposer un fichier Excel ici'}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        ou cliquer pour parcourir — formats acceptés : .xlsx, .xls
      </Typography>
      {parsing && <LinearProgress sx={{ mt: 3 }} />}
      <Alert severity="info" sx={{ mt: 3, textAlign: 'left' }}>
        La première ligne du fichier doit contenir les en-têtes de colonnes
        (Référence, Désignation, Catégorie, Unité…). Vous pourrez ajuster le
        mapping à l'étape suivante.
      </Alert>
    </Box>
  );

  const renderMapping = () => (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Fichier : <strong>{fileName}</strong> — {rawRows.length} ligne{rawRows.length > 1 ? 's' : ''} détectée{rawRows.length > 1 ? 's' : ''}.
        Associer chaque colonne du fichier à un champ d'article.
      </Typography>
      <TableContainer component={Paper} sx={getModuleTableTheme('configuration')}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={modernCellStyles}>Colonne Excel</TableCell>
              <TableCell sx={modernCellStyles}>Aperçu (1ère ligne)</TableCell>
              <TableCell sx={modernCellStyles}>Champ d'article</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {headers.map((h, i) => (
              <TableRow key={i}>
                <TableCell><strong>{h || `Colonne ${i + 1}`}</strong></TableCell>
                <TableCell sx={{ color: 'text.secondary' }}>
                  {rawRows[0]?.cells[i] ?? ''}
                </TableCell>
                <TableCell>
                  <FormControl size="small" fullWidth>
                    <Select
                      value={columnMapping[i] ?? '__ignore__'}
                      onChange={e => {
                        const next = [...columnMapping];
                        next[i] = e.target.value as ArticleField;
                        setColumnMapping(next);
                      }}
                    >
                      {(Object.keys(FIELD_LABELS) as ArticleField[]).map(f => (
                        <MenuItem key={f} value={f}>
                          {FIELD_LABELS[f]}
                          {REQUIRED_FIELDS.includes(f) ? ' *' : ''}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap' }}>
        {REQUIRED_FIELDS.map(f => {
          const mapped = columnMapping.includes(f);
          return (
            <Chip
              key={f}
              size="small"
              icon={mapped ? <CheckIcon /> : <ErrorIcon />}
              color={mapped ? 'success' : 'error'}
              label={`${FIELD_LABELS[f]} ${mapped ? '✓' : 'manquant'}`}
              variant={mapped ? 'filled' : 'outlined'}
            />
          );
        })}
      </Stack>
    </Box>
  );

  const renderPreview = () => {
    const importableCount = importableRows.length;
    const skippedCount = rows.filter(r => r.skip).length;
    const invalidCount = blockingRows.length;
    const duplicateCount = rows.filter(r => r.isDuplicate).length;

    return (
      <Box>
        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
          <Chip icon={<CheckIcon />} color="success" label={`${importableCount} prêt${importableCount > 1 ? 's' : ''}`} />
          {invalidCount > 0 && (
            <Chip icon={<ErrorIcon />} color="error" label={`${invalidCount} bloqué${invalidCount > 1 ? 's' : ''}`} />
          )}
          {duplicateCount > 0 && (
            <Chip icon={<WarningIcon />} color="warning" label={`${duplicateCount} déjà existant${duplicateCount > 1 ? 's' : ''}`} />
          )}
          {skippedCount > 0 && (
            <Chip label={`${skippedCount} ignoré${skippedCount > 1 ? 's' : ''}`} variant="outlined" />
          )}
        </Stack>

        {invalidCount > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {invalidCount} ligne{invalidCount > 1 ? 's' : ''} non importable{invalidCount > 1 ? 's' : ''} —
            assigner une catégorie / unité pour les corriger, ou les cocher comme ignorées.
          </Alert>
        )}

        <TableContainer component={Paper} sx={{ ...getModuleTableTheme('configuration'), maxHeight: 500 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={modernCellStyles} padding="checkbox">Ignorer</TableCell>
                <TableCell sx={modernCellStyles}>#</TableCell>
                <TableCell sx={modernCellStyles}>Référence</TableCell>
                <TableCell sx={modernCellStyles}>Désignation</TableCell>
                <TableCell sx={modernCellStyles}>Catégorie</TableCell>
                <TableCell sx={modernCellStyles}>Unité</TableCell>
                <TableCell sx={modernCellStyles}>Seuil</TableCell>
                <TableCell sx={modernCellStyles}>Date</TableCell>
                <TableCell sx={modernCellStyles}>Statut</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r, i) => {
                const validationError = validateRow(r);
                const rowBg = r.skip
                  ? 'action.disabledBackground'
                  : validationError
                  ? 'error.lighter'
                  : r.isDuplicate
                  ? 'warning.lighter'
                  : 'transparent';
                return (
                  <TableRow key={i} sx={{ bgcolor: rowBg }}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={r.skip}
                        onChange={e => updateRow(i, { skip: e.target.checked })}
                      />
                    </TableCell>
                    <TableCell>{r.rowIndex}</TableCell>
                    <TableCell>{r.reference || <em style={{ opacity: 0.5 }}>vide</em>}</TableCell>
                    <TableCell sx={{ maxWidth: 280, whiteSpace: 'normal' }}>
                      {r.designation || <em style={{ opacity: 0.5 }}>vide</em>}
                    </TableCell>
                    <TableCell>
                      {r.category_id !== '' ? (
                        <Chip
                          size="small"
                          color="success"
                          variant="outlined"
                          label={categories.find(c => c.id === r.category_id)?.name ?? '—'}
                        />
                      ) : (
                        <FormControl size="small" sx={{ minWidth: 160 }}>
                          <Select
                            displayEmpty
                            value=""
                            onChange={e => updateRow(i, { category_id: Number(e.target.value) })}
                            renderValue={() => (
                              <Typography variant="caption" color="error">
                                « {r.rawCategory || 'vide'} » → choisir
                              </Typography>
                            )}
                          >
                            {categories.map(c => (
                              <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    </TableCell>
                    <TableCell>
                      {r.unit_id !== '' ? (
                        <Chip
                          size="small"
                          color="success"
                          variant="outlined"
                          label={units.find(u => u.id === r.unit_id)?.name ?? '—'}
                        />
                      ) : (
                        <FormControl size="small" sx={{ minWidth: 140 }}>
                          <Select
                            displayEmpty
                            value=""
                            onChange={e => updateRow(i, { unit_id: Number(e.target.value) })}
                            renderValue={() => (
                              <Typography variant="caption" color="error">
                                « {r.rawUnit || 'vide'} » → choisir
                              </Typography>
                            )}
                          >
                            {units.map(u => (
                              <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    </TableCell>
                    <TableCell>{r.seuil_alerte}</TableCell>
                    <TableCell>{r.date_creation}</TableCell>
                    <TableCell>
                      {r.isDuplicate ? (
                        <Tooltip title="Cette référence existe déjà dans la base">
                          <Chip size="small" color="warning" label="Déjà existant" />
                        </Tooltip>
                      ) : validationError ? (
                        <Tooltip title={validationError}>
                          <Chip size="small" color="error" label="À corriger" />
                        </Tooltip>
                      ) : (
                        <Chip size="small" color="success" variant="outlined" label="Prêt" />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const renderImport = () => {
    const successCount = rows.filter(r => r.status === 'success').length;
    const errorCount = rows.filter(r => r.status === 'error').length;
    const skippedCount = rows.filter(r => r.skip).length;
    return (
      <Box>
        {importing && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Import en cours… ({Math.round(progress)}%)
            </Typography>
            <LinearProgress variant="determinate" value={progress} />
          </Box>
        )}
        {importDone && (
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Chip icon={<CheckIcon />} color="success" label={`${successCount} importé${successCount > 1 ? 's' : ''}`} />
            {errorCount > 0 && (
              <Chip icon={<ErrorIcon />} color="error" label={`${errorCount} échoué${errorCount > 1 ? 's' : ''}`} />
            )}
            {skippedCount > 0 && (
              <Chip label={`${skippedCount} ignoré${skippedCount > 1 ? 's' : ''}`} variant="outlined" />
            )}
          </Stack>
        )}

        <TableContainer component={Paper} sx={{ ...getModuleTableTheme('configuration'), maxHeight: 500 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={modernCellStyles}>#</TableCell>
                <TableCell sx={modernCellStyles}>Référence</TableCell>
                <TableCell sx={modernCellStyles}>Désignation</TableCell>
                <TableCell sx={modernCellStyles}>Statut</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r, i) => (
                <TableRow key={i}>
                  <TableCell>{r.rowIndex}</TableCell>
                  <TableCell>{r.reference}</TableCell>
                  <TableCell sx={{ maxWidth: 320, whiteSpace: 'normal' }}>{r.designation}</TableCell>
                  <TableCell>
                    {r.status === 'success' && (
                      <Chip size="small" color="success" icon={<CheckIcon />} label="Importé" />
                    )}
                    {r.status === 'error' && (
                      <Tooltip title={r.errorMessage ?? 'Erreur'}>
                        <Chip size="small" color="error" icon={<ErrorIcon />} label="Échec" />
                      </Tooltip>
                    )}
                    {r.status === 'skipped' && (
                      <Chip size="small" label="Ignoré" variant="outlined" />
                    )}
                    {r.status === 'pending' && (
                      <Chip size="small" label="En attente" variant="outlined" />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const requiredFieldsMapped = REQUIRED_FIELDS.every(f => columnMapping.includes(f));
  const canPreview = step === 1 && requiredFieldsMapped;
  const canImport = step === 2 && importableRows.length > 0 && blockingRows.length === 0;

  return (
    <Dialog open={open} onClose={importing ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Importer des articles depuis Excel</DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={step} sx={{ mb: 3 }}>
          {STEPS.map(label => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {loadingMeta ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {step === 0 && renderUpload()}
            {step === 1 && renderMapping()}
            {step === 2 && renderPreview()}
            {step === 3 && renderImport()}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {step === 3 && importDone ? (
          <Button onClick={closeAfterImport} variant="contained">Fermer</Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={importing}>Annuler</Button>
            {step === 1 && (
              <>
                <Button onClick={() => setStep(0)}>Retour</Button>
                <Button
                  variant="contained"
                  onClick={buildPreviewRows}
                  disabled={!canPreview}
                >
                  Continuer
                </Button>
              </>
            )}
            {step === 2 && (
              <>
                <Button onClick={() => setStep(1)}>Retour</Button>
                <Button
                  variant="contained"
                  onClick={handleImport}
                  disabled={!canImport}
                >
                  Importer {importableRows.length} article{importableRows.length > 1 ? 's' : ''}
                </Button>
              </>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ArticleBulkImportDialog;
